import { SubscriptionPlan } from '@repo/domain/user';

import { State } from './types';

export const userStoreSelector = {
  user: (state: State): Exclude<State['user'], false> =>
    state.user ? state.user : null,
  /**
   *
   * @returns user object if set, null if no user, false if unclear
   */
  loadingUser: (state: State): State['user'] => state.user,
  userId: (state: State): string | false | null =>
    state.user ? state.user.id : state.user,
  /**
   * Returns true if the user has been fetched
   */
  isUserLoaded: (state: State): boolean => state.user !== false,
  /**
   * Returns true while user is loading
   */
  isUserLoading: (state: State): boolean => state.isLoading,
  /**
   * Returns true if the user is anonymous (not logged in)
   */
  isAnonymousUser: (state: State): boolean => state.user === null,
  /**
   * Returns true if the user is an admin in the team and false if not. It returns true for non-team users.
   */
  canUpdateUserOrTeamAccountSettings: ({ user }: State): boolean => {
    if (user) {
      return user.isTeam
        ? user.user.teams?.find((team) => team.slug === user.slug)?.role ===
            'ADMIN'
        : true;
    }
    return false;
  },
  isSigningOut: (state: State): boolean => state.isSigningOut,
  anonymousUserName: (state: State): string | undefined =>
    state.anonymousUserName,
  currentCountry: (state: State): string | null =>
    state.user ? (state.user?.profile?.country?.current ?? null) : null,
  plan: (state: State): SubscriptionPlan | undefined =>
    state.user ? state.user?.plan : undefined,
  proAccess: ({ user }: State): boolean =>
    user ? user.plan === 'PRO' || user.plan === 'EXPERT' : false,
  initialize: (state: State): State['initialize'] => state.initialize,
  registerBeforeLoginAction: (
    state: State
  ): State['registerBeforeLoginAction'] => state.registerBeforeLoginAction,
  unregisterBeforeLoginAction: (
    state: State
  ): State['unregisterBeforeLoginAction'] => state.unregisterBeforeLoginAction,
  isLoggedIn: (state: State): boolean => !!state.user,
  logout: (state: State): State['logout'] => state.logout,
  updateUser: (state: State): State['updateUser'] => state.updateUser,
  updateUserInfo: (state: State): State['updateUserInfo'] =>
    state.updateUserInfo,
  updateUserCreditInfo: (state: State): State['updateUserCreditInfo'] =>
    state.updateUserCreditInfo,
  createUser: (state: State): State['createUser'] => state.createUser,
  loginUser: (state: State): State['loginUser'] => state.loginUser,
  loginTeam: (state: State): State['loginTeam'] => state.loginTeam,
  swapMainUser: (state: State): State['swapMainUser'] => state.swapMainUser,
  exchangeTemporaryToken: (state: State): State['exchangeTemporaryToken'] =>
    state.exchangeTemporaryToken,
  bookmarks: (state: State): State['bookmarks'] => state.bookmarks,
  updateBookmark: (state: State): State['updateBookmark'] =>
    state.updateBookmark,
  isTeam: (state: State): boolean => Boolean(state.user && state.user.isTeam),
  getProfileToken: (state: State): State['getProfileToken'] =>
    state.getProfileToken,
  setProfileToken: (state: State): State['setProfileToken'] =>
    state.setProfileToken,
  getAttribute: (state: State): State['getAttribute'] => state.getAttribute,
  fetchAttribute: (state: State): State['fetchAttribute'] =>
    state.fetchAttribute,
  createUpdateAttribute: (state: State): State['createUpdateAttribute'] =>
    state.createUpdateAttribute,
  setAnonymousName: (state: State): State['setAnonymousName'] =>
    state.setAnonymousName,
  setIsSigningOut: (state: State): State['setIsSigningOut'] =>
    state.setIsSigningOut,
};
